/**
 * NPC daily life (ชีวิตประจำวันของชาวบ้าน, lane C0-NPC phase N1; design docs/design/C0_NPC_LIFE.html §04, research
 * docs/research/NPC_LIFE.md): the people of the plains who live by five needs layered over a default plan for each
 * part of the day. Each person picks the act that suits them best right now — the plan (schedule) is the default,
 * hunger, tiredness, the urge to work, to talk or to train change it — walks to its place and does it: eating together
 * at the inn and the kitchen stall, mending nets, tea, chess and the zither, sweeping the shrine and lighting incense,
 * children chasing and hiding and copying the martial, farmers ploughing behind the buffalo, planting and
 * harvesting, fishermen casting nets, washing at the steps, disciples sparring and meditating.
 *
 * Ties (family, friends, rivals, master and pupil) pull people to the same place or keep them apart, and rumours of
 * what the player did pass from person to person (`RUMOUR_LINES`).
 *
 * Data only: `systems/npcLife.ts` runs it. The client draws an act through the emote channel (`lifeEmote`),
 * its poses in apps/client0/src/render/motions/life.ts and its art in render/lifeArt.ts; sounds `life.<sound>` in
 * apps/client0/src/sfx/sounds/life.ts.
 */
import type { DayPart } from './folk';
import type { NpcRole } from './npcActs';
import type { XY } from './layouts/plains';
import type { ZoneId } from './zones';

// ───────────────────────── needs ─────────────────────────

export type LifeNeed = 'fed' | 'rest' | 'work' | 'social' | 'train';
export const LIFE_NEEDS: readonly LifeNeed[] = ['fed', 'rest', 'work', 'social', 'train'];
export const NEED_NAMES: Readonly<Record<LifeNeed, string>> = {
  fed: 'อิ่ม',
  rest: 'พัก',
  work: 'งาน',
  social: 'สังคม',
  train: 'ฝึกฝน',
};

/** The rules of the needs and the picking (see docs/research/NPC_LIFE.md §2). */
export const LIFE = {
  /** Points each need loses per in-game hour (a need runs 0–100, 100 met). `rest` only while awake. */
  decay: { fed: 11, rest: 6, work: 9, social: 8, train: 9 } satisfies Record<LifeNeed, number>,
  /** Real seconds in an in-game hour (data/time.ts: a day every 3600 s). */
  hourSeconds: 150,
  /** How strongly the plan (schedule) weighs against the needs. */
  planK: 3,
  /** Urgency of a need: ((100 − v) / 100)² × this. */
  urgencyK: 4,
  /** Refill points per in-game hour are divided by this to weigh against the plan. */
  giveScale: 30,
  /** A tie at the act's place: + for family/friends/master, − for a rival. */
  tieWith: 0.8,
  rivalAvoid: 1.4,
  /** Random spread added to every score (keeps a crowd from moving in lockstep). */
  jitter: 0.5,
  /** Every metre to walk costs this much score (people prefer what is near). */
  farCost: 0.006,
  /** A need below this breaks off an act that does not refill it. */
  critical: 15,
  /** Within this of the player (metres, both axes): full thinking every tick ("two screens"). */
  nearR: 24,
  /** Farther away: thinking once a second, straight-line travel. */
  farTick: 1,
  /** Path searches a tick may start (all people together), and the longest leg one search covers (m). */
  pathsPerTick: 2,
  legMax: 30,
  /** Seconds between barks of one person (only heard within `hearing`). */
  barkEvery: [26, 48] as const,
  hearing: 13,
  /** A passer-by this close is told a rumour (metres). */
  tell: 3.2,
  /** Work sounds are sent only this close to the player (metres). */
  soundR: 11,
} as const;

// ───────────────────────── places ─────────────────────────

export type LifePlace =
  | 'home'
  | 'inn'
  | 'stall'
  | 'opera'
  | 'shrine'
  | 'retreat'
  | 'play'
  | 'paddy'
  | 'buffalo'
  | 'fishSpot'
  | 'wash'
  | 'market'
  | 'trainhall'
  | 'pavilion'
  | 'garden'
  | 'plaza'
  | 'woods'
  /** A boat lane's landing (the 256 m map's `l-boat-*` stations): the fisherman steps into a boat there. */
  | 'boat';

/**
 * Where a place is on a map. `big`: metres on the 256 m plains (data/layouts/plains.ts); `station`: a life station of
 * that map (`LIFE_STATIONS`); `mark`: a landmark of any map (`plaza`: the map's plaza), shifted; `nearTile`: the open
 * tile beside the nearest water / paddy / tree tile to a landmark, within `within` metres. `map` keeps a source to one
 * map (`small`: the 72 m map, the fallback where the big map has the real place). `n`: people it holds.
 */
export type PlaceSource = {
  readonly map?: 'big' | 'small';
  readonly n?: number;
  readonly r?: number;
  /** How far to look for open ground from the point (m; 4 if left out): a landing on the bank of a pond. */
  readonly reach?: number;
} & (
  | { readonly big: XY }
  | { readonly station: string }
  | { readonly mark: string; readonly dx?: number; readonly dy?: number }
  | { readonly nearTile: 'water' | 'paddy' | 'tree'; readonly from: string; readonly within: number }
);

/**
 * Plaza-side spots stand 9 m or more off the plaza's middle: the 72 m map's duel ring and the stele are kept clear.
 */
export const LIFE_PLACES: Readonly<Record<Exclude<LifePlace, 'home'>, readonly PlaceSource[]>> = {
  inn: [
    { mark: 'inn', dx: -3.5, dy: 1.6, r: 1.2, n: 4 },
    { mark: 'inn', dx: 3.5, dy: 1.6, r: 1.2, n: 4 },
    { mark: 'inn', dx: 0, dy: 3.2, r: 1, n: 3 },
    { map: 'small', mark: 'pavilion', dx: 1.5, dy: 1.2, r: 1, n: 4 },
  ],
  stall: [
    { big: [170, 134.8], r: 1, n: 3 },
    { big: [178, 134.8], r: 1, n: 2 },
    { map: 'small', mark: 'plaza', dx: 9, dy: 6, r: 1, n: 3 },
  ],
  opera: [{ big: [166, 108.5], r: 3, n: 8 }],
  shrine: [
    { station: 'l-town-shrine', r: 2, n: 3 },
    { map: 'small', mark: 'pagoda', dx: 0, dy: 1.5, r: 1.2, n: 3 },
  ],
  retreat: [
    { big: [204, 156], r: 2, n: 3 },
    { map: 'small', mark: 'pagoda', dx: 3, dy: 2.5, r: 1.2, n: 2 },
  ],
  play: [
    { station: 'l-play-opera', r: 3, n: 5 },
    { station: 'l-play-homes', r: 2, n: 3 },
    { map: 'small', mark: 'plaza', dx: -10, dy: 7, r: 2, n: 5 },
  ],
  paddy: [
    { big: [116, 200], r: 3, n: 2 },
    { big: [136, 204], r: 3, n: 2 },
    { big: [126, 196], r: 3, n: 2 },
    { map: 'small', nearTile: 'paddy', from: 'plaza', within: 34, r: 1.5, n: 3 },
    { map: 'small', mark: 'house20', dx: 3, dy: 3, r: 1.5, n: 2 },
  ],
  buffalo: [
    { station: 'l-buffalo-1', r: 1.5, n: 1 },
    { station: 'l-buffalo-2', r: 1.5, n: 1 },
  ],
  boat: [
    { station: 'l-boat-stream', r: 0.5, n: 1, reach: 10 },
    { station: 'l-boat-pond', r: 0.5, n: 1, reach: 10 },
  ],
  fishSpot: [
    { station: 'l-fish-pond', r: 1.5, n: 2 },
    { station: 'l-fish-stream', r: 1.5, n: 2 },
    { map: 'small', nearTile: 'water', from: 'plaza', within: 34, r: 1, n: 2 },
  ],
  wash: [
    { station: 'l-wash', r: 2, n: 3 },
    { map: 'small', nearTile: 'water', from: 'house13', within: 34, r: 1.2, n: 2 },
  ],
  market: [
    { big: [166, 127], r: 1, n: 2 },
    { big: [176, 127], r: 1, n: 2 },
    { big: [188, 127], r: 1, n: 2 },
    { big: [198, 127], r: 1, n: 2 },
    { map: 'small', mark: 'plaza', dx: 9, dy: -7, r: 1.5, n: 3 },
  ],
  trainhall: [
    { big: [80, 119], r: 1.6, n: 4 },
    { big: [92, 119], r: 1.6, n: 4 },
    { big: [86, 122.5], r: 1.6, n: 4 },
    { map: 'small', mark: 'plaza', dx: -11, dy: -6, r: 1.8, n: 4 },
  ],
  pavilion: [
    { big: [134, 101.2], r: 1, n: 3 },
    { big: [158, 99.2], r: 1, n: 3 },
    { big: [214, 135.2], r: 1, n: 2 },
    { big: [134, 183.2], r: 1, n: 3 },
    { map: 'small', mark: 'pavilion', r: 1.2, n: 4 },
  ],
  garden: [
    { big: [180, 87], r: 2.5, n: 3 },
    { map: 'small', mark: 'pagoda', dx: -2.5, dy: 1.5, r: 1, n: 2 },
  ],
  plaza: [
    { mark: 'plaza', dx: -10, dy: 3, r: 1.5, n: 3 },
    { mark: 'plaza', dx: 10, dy: 3, r: 1.5, n: 3 },
    { mark: 'plaza', dx: 0, dy: -10, r: 1.5, n: 3 },
  ],
  woods: [
    { big: [186, 70], r: 2.5, n: 2 },
    { big: [214, 70], r: 2.5, n: 1 },
    { map: 'small', nearTile: 'tree', from: 'house5', within: 30, r: 1, n: 2 },
  ],
};

/** The boats on the lanes (the client draws `sprite` from the C0-PROPS kit: sampan, net boat), by station. */
export const LIFE_BOATS: Readonly<Record<string, { readonly sprite: string; readonly speed: number }>> = {
  'l-boat-stream': { sprite: 'boatNet', speed: 0.45 },
  'l-boat-pond': { sprite: 'boatSampan', speed: 0.35 },
};

/** The doors people live behind on the 72 m map (its landmarks); the 256 m map uses its houses (layouts/plains.ts). */
export const SMALL_HOMES: readonly string[] = ['house1', 'house5', 'house13', 'house20'];

// ───────────────────────── acts ─────────────────────────

export type LifeJob =
  | 'farmer'
  | 'fisher'
  | 'washer'
  | 'vendor'
  | 'cook'
  | 'innkeeper'
  | 'keeper'
  | 'woodcutter'
  | 'elder'
  | 'musician'
  | 'child'
  | 'disciple'
  | 'teacher';

/** The martial: they have the `train` need and the training-hall acts. */
export const MARTIAL_JOBS: readonly LifeJob[] = ['disciple', 'teacher'];

export interface LifeActDef {
  /** Thai, for tools and the target frame. */
  readonly name: string;
  readonly place: LifePlace;
  /** Points refilled per in-game hour, by need (a negative is a cost: hard work tires). */
  readonly gives: Partial<Record<LifeNeed, number>>;
  /** Real seconds held (a random span). */
  readonly dur: readonly [number, number];
  /** Who does it; left out: every grown-up. */
  readonly jobs?: readonly LifeJob[];
  /** Only in these parts of the day. */
  readonly parts?: readonly DayPart[];
  /** Not in rain, storm or snow. */
  readonly dry?: boolean;
  /** Done in company: people at it together pass on rumours and talk (`CHAT_LINES`). */
  readonly social?: boolean;
  /** Indoors, out of sight (sleep). */
  readonly indoors?: boolean;
  /** The body moves at the spot: chasing round in a ring, or ambling about. */
  readonly move?: 'circle' | 'wander';
  /** Its work sound (`life.<sound>` in the client) and how often it sounds (seconds), while the player is near. */
  readonly sound?: string;
  readonly beat?: number;
  /** Facing the water (fishing, washing, the nets): turned toward the nearest water tile on arrival. */
  readonly faceWater?: boolean;
  /** Never picked by the needs: set by the system for a reason (an injury, a visit to the injured). */
  readonly special?: boolean;
}

const ADULTS_CIVIL: readonly LifeJob[] = [
  'farmer',
  'fisher',
  'washer',
  'vendor',
  'cook',
  'innkeeper',
  'keeper',
  'woodcutter',
  'elder',
  'musician',
];
const ALL_ADULTS: readonly LifeJob[] = [...ADULTS_CIVIL, 'disciple', 'teacher'];
const DAY: readonly DayPart[] = ['dawn', 'day'];
const AWAKE: readonly DayPart[] = ['dawn', 'day', 'dusk'];

/** Every life act (≈40), by id. The client's emote id is `lifeEmote(id)`. */
const ACTS = {
  // ── fed: eating, together where they can ──
  eatInn: { name: 'กินข้าวที่โรงเตี๊ยม', place: 'inn', gives: { fed: 150, social: 25 }, dur: [45, 75], social: true, parts: ['day', 'dusk'] },
  eatStall: { name: 'กินบะหมี่ที่แผงครัว', place: 'stall', gives: { fed: 140, social: 15 }, dur: [35, 60], social: true, parts: AWAKE },
  eatHome: { name: 'กินข้าวหน้าบ้าน', place: 'home', gives: { fed: 130, social: 10 }, dur: [40, 60], social: true, jobs: [...ALL_ADULTS, 'child'], parts: ['dawn', 'dusk'] },
  snack: { name: 'กินซาลาเปา', place: 'market', gives: { fed: 90 }, dur: [15, 25], jobs: [...ALL_ADULTS, 'child'], parts: AWAKE },
  fieldLunch: { name: 'กินข้าวห่อกลางนา', place: 'paddy', gives: { fed: 130, rest: 10 }, dur: [35, 55], jobs: ['farmer'], parts: ['day'], dry: true },
  // ── rest ──
  sleep: { name: 'นอนหลับที่บ้าน', place: 'home', gives: { rest: 22 }, dur: [200, 320], indoors: true, jobs: [...ALL_ADULTS, 'child'] },
  nap: { name: 'งีบใต้ร่มศาลา', place: 'pavilion', gives: { rest: 45 }, dur: [40, 70], parts: ['day'], jobs: [...ALL_ADULTS, 'child'] },
  sitRest: { name: 'นั่งพักเหนื่อย', place: 'plaza', gives: { rest: 30, social: 10 }, dur: [25, 45], parts: AWAKE },
  // ── work: the farmers ──
  plough: { name: 'ไถนากับควาย', place: 'buffalo', gives: { work: 60, rest: -8 }, dur: [70, 120], jobs: ['farmer'], parts: DAY, dry: true, sound: 'plough', beat: 2.2 },
  plantRice: { name: 'ดำนา', place: 'paddy', gives: { work: 55, rest: -6 }, dur: [60, 110], jobs: ['farmer'], parts: DAY, dry: true, sound: 'splash', beat: 1.6 },
  harvest: { name: 'เกี่ยวข้าว', place: 'paddy', gives: { work: 55, rest: -6 }, dur: [60, 110], jobs: ['farmer'], parts: ['day'], dry: true, sound: 'sickle', beat: 1.3 },
  tillSoil: { name: 'พรวนดิน', place: 'paddy', gives: { work: 50, rest: -6 }, dur: [50, 90], jobs: ['farmer'], parts: DAY, sound: 'hoe', beat: 1.5 },
  // ── work: the water ──
  boatNet: { name: 'ทอดแหจากเรือ', place: 'boat', gives: { work: 65 }, dur: [70, 120], jobs: ['fisher'], parts: AWAKE, dry: true, sound: 'net', beat: 4.2 },
  castNet: { name: 'ทอดแห', place: 'fishSpot', gives: { work: 55 }, dur: [60, 100], jobs: ['fisher'], parts: AWAKE, faceWater: true, sound: 'net', beat: 4.2 },
  fishRod: { name: 'ตกปลา', place: 'fishSpot', gives: { work: 40, rest: 10 }, dur: [70, 120], jobs: ['fisher', 'elder'], faceWater: true },
  mendNet: { name: 'ซ่อมแห', place: 'home', gives: { work: 45, social: 5 }, dur: [50, 90], jobs: ['fisher'], parts: AWAKE },
  washClothes: { name: 'ซักผ้าริมน้ำ', place: 'wash', gives: { work: 55, social: 15 }, dur: [60, 100], jobs: ['washer'], parts: DAY, social: true, faceWater: true, sound: 'wash', beat: 1.4 },
  hangLaundry: { name: 'ตากผ้า', place: 'home', gives: { work: 40 }, dur: [30, 50], jobs: ['washer'], parts: ['day'], dry: true },
  // ── work: trade and the kitchen ──
  hawk: { name: 'ร้องขายของ', place: 'market', gives: { work: 50, social: 15 }, dur: [60, 100], jobs: ['vendor'], parts: AWAKE },
  cook: { name: 'ผัดอาหารที่แผงครัว', place: 'stall', gives: { work: 55 }, dur: [70, 120], jobs: ['cook'], parts: AWAKE, sound: 'wok', beat: 1.1 },
  serve: { name: 'ยกอาหารเสิร์ฟ', place: 'inn', gives: { work: 50, social: 10 }, dur: [50, 90], jobs: ['innkeeper'], parts: ['day', 'dusk'] },
  sweepYard: { name: 'กวาดหน้าบ้าน', place: 'home', gives: { work: 35 }, dur: [25, 45], jobs: ADULTS_CIVIL, parts: ['dawn', 'day'], sound: 'sweep', beat: 1.1 },
  // ── work: the shrine and the woods ──
  sweepShrine: { name: 'กวาดลานศาลเจ้า', place: 'shrine', gives: { work: 50 }, dur: [50, 90], jobs: ['keeper'], parts: DAY, sound: 'sweep', beat: 1.1 },
  chopWood: { name: 'ตัดไม้', place: 'woods', gives: { work: 60, rest: -10 }, dur: [70, 120], jobs: ['woodcutter'], parts: DAY, sound: 'chop', beat: 1.5 },
  carryWood: { name: 'แบกฟืนไปส่งช่าง', place: 'market', gives: { work: 40, rest: -5 }, dur: [20, 35], jobs: ['woodcutter'], parts: ['day', 'dusk'] },
  // ── social ──
  tea: { name: 'จิบชา', place: 'pavilion', gives: { social: 35, rest: 15 }, dur: [40, 70], social: true },
  innTea: { name: 'ดื่มชาที่โรงเตี๊ยม', place: 'inn', gives: { social: 40, rest: 10 }, dur: [40, 70], social: true, parts: ['day', 'dusk', 'night'] },
  chess: { name: 'เล่นหมากรุก', place: 'pavilion', gives: { social: 45, rest: 10 }, dur: [70, 120], social: true, jobs: ['elder', 'keeper', 'musician', 'teacher'], parts: ['day', 'dusk'], dry: true, sound: 'chess', beat: 5 },
  zither: { name: 'ดีดพิณ', place: 'garden', gives: { social: 30, rest: 20 }, dur: [60, 100], jobs: ['musician', 'elder'], parts: AWAKE, dry: true, sound: 'zither', beat: 3.2 },
  gossip: { name: 'ซุบซิบ', place: 'plaza', gives: { social: 45 }, dur: [35, 60], social: true, parts: AWAKE },
  marketChat: { name: 'ต่อราคาที่ตลาด', place: 'market', gives: { social: 35, fed: 10 }, dur: [25, 45], social: true, jobs: ADULTS_CIVIL, parts: ['dawn', 'day'] },
  watchOpera: { name: 'ดูงิ้ว', place: 'opera', gives: { social: 50, rest: 10 }, dur: [60, 100], social: true, jobs: [...ALL_ADULTS, 'child'], parts: ['dusk'], dry: true },
  incense: { name: 'จุดธูปไหว้', place: 'shrine', gives: { social: 20, rest: 10 }, dur: [20, 35], parts: AWAKE, sound: 'bell', beat: 9 },
  pray: { name: 'ไหว้ขอพร', place: 'shrine', gives: { social: 15, rest: 15 }, dur: [15, 25], parts: AWAKE },
  storyKids: { name: 'เล่านิทานให้เด็กฟัง', place: 'play', gives: { social: 50 }, dur: [50, 80], social: true, jobs: ['elder'], parts: ['day', 'dusk'] },
  // ── the children ──
  chase: { name: 'วิ่งไล่จับ', place: 'play', gives: { social: 50, rest: -10 }, dur: [30, 50], social: true, jobs: ['child'], parts: AWAKE, dry: true, move: 'circle' },
  hideSeek: { name: 'เล่นซ่อนหา', place: 'play', gives: { social: 50 }, dur: [30, 50], social: true, jobs: ['child'], parts: AWAKE, dry: true, move: 'wander' },
  woodSword: { name: 'ถือดาบไม้เลียนท่าจอมยุทธ์', place: 'play', gives: { social: 30, work: 30 }, dur: [25, 45], jobs: ['child'], parts: AWAKE, dry: true, sound: 'stick', beat: 0.9 },
  shuttle: { name: 'เตะลูกขนไก่', place: 'play', gives: { social: 40 }, dur: [25, 40], social: true, jobs: ['child'], parts: AWAKE, dry: true, sound: 'kick', beat: 1.2 },
  splash: { name: 'เล่นน้ำตื้น', place: 'wash', gives: { social: 35, rest: 5 }, dur: [25, 40], jobs: ['child'], parts: ['day'], dry: true, faceWater: true, sound: 'splash', beat: 1.8 },
  helpChores: { name: 'ช่วยพ่อแม่ทำงาน', place: 'home', gives: { work: 50 }, dur: [25, 45], jobs: ['child'], parts: DAY, sound: 'sweep', beat: 1.3 },
  // ── the martial ──
  spar: { name: 'ประมือซ้อม', place: 'trainhall', gives: { train: 70, rest: -10, social: 10 }, dur: [50, 90], jobs: MARTIAL_JOBS, parts: AWAKE, social: true, sound: 'spar', beat: 0.75 },
  drillForm: { name: 'ซ้อมกระบวนท่า', place: 'trainhall', gives: { train: 60, rest: -6 }, dur: [50, 90], jobs: MARTIAL_JOBS, parts: AWAKE, sound: 'whoosh', beat: 1.6 },
  horseStance: { name: 'ยืนม้า', place: 'trainhall', gives: { train: 50, rest: -8 }, dur: [40, 70], jobs: MARTIAL_JOBS, parts: ['dawn', 'day'] },
  meditate: { name: 'นั่งสมาธิ', place: 'retreat', gives: { train: 45, rest: 25 }, dur: [60, 110], jobs: MARTIAL_JOBS },
  meditateHall: { name: 'นั่งสมาธิในโรงฝึก', place: 'trainhall', gives: { train: 40, rest: 20 }, dur: [50, 90], jobs: MARTIAL_JOBS },
  // ── after a fight (N2, systems/npcLifeEvents.ts): resting a day at home, family and friends calling on the injured ──
  recover: { name: 'พักฟื้นที่บ้าน', place: 'home', gives: { rest: 20 }, dur: [600, 900], indoors: true, special: true, jobs: [...ALL_ADULTS, 'child'] },
  visitSick: { name: 'เยี่ยมคนเจ็บ', place: 'home', gives: { social: 40 }, dur: [25, 40], special: true, social: true, jobs: [...ALL_ADULTS, 'child'] },
  teach: { name: 'คุมศิษย์ซ้อม', place: 'trainhall', gives: { work: 60, train: 20, social: 20 }, dur: [70, 120], jobs: ['teacher'], parts: AWAKE, social: true },
} as const satisfies Record<string, LifeActDef>;

export type LifeActId = keyof typeof ACTS;
export const LIFE_ACTS: Readonly<Record<LifeActId, LifeActDef>> = ACTS;
export const LIFE_ACT_IDS = Object.keys(ACTS) as LifeActId[];
export const isLifeAct = (v: unknown): v is LifeActId => typeof v === 'string' && Object.hasOwn(ACTS, v);

/** The emote id an act is drawn with (client render/lifeArt.ts keys its art by it). */
export const lifeEmote = (id: LifeActId): string => `life-${id}`;
/** The act of a `life-*` emote id, or null. */
export const lifeActOfEmote = (emote: string | null | undefined): LifeActId | null => {
  if (!emote || !emote.startsWith('life-')) return null;
  const id = emote.slice(5);
  return isLifeAct(id) ? id : null;
};

// ───────────────────────── plans (the default schedule) ─────────────────────────

/** How much a person means to do each act in a part of the day (0–3): the schedule, before the needs. */
export type LifePlan = Readonly<Record<DayPart, Partial<Record<LifeActId, number>>>>;

const NIGHT_HOME = { sleep: 3 } as const;

/** Each job's default plan. */
export const JOB_PLANS: Readonly<Record<LifeJob, LifePlan>> = {
  farmer: {
    dawn: { eatHome: 1.5, plough: 1.5, tillSoil: 1, sweepYard: 0.5 },
    day: { plough: 2, plantRice: 2, harvest: 1.5, tillSoil: 1, fieldLunch: 1, nap: 0.3 },
    dusk: { eatHome: 1.5, eatInn: 0.8, gossip: 1, watchOpera: 0.7, innTea: 0.5 },
    night: NIGHT_HOME,
  },
  fisher: {
    dawn: { castNet: 2, boatNet: 1.5, fishRod: 1.5, eatHome: 0.8 },
    day: { castNet: 1.5, boatNet: 1.5, fishRod: 1.5, mendNet: 1.5, snack: 0.5, eatStall: 0.7, nap: 0.4 },
    dusk: { eatInn: 1.5, innTea: 1, mendNet: 0.8, gossip: 0.6 },
    night: { sleep: 3, fishRod: 0.3 },
  },
  washer: {
    dawn: { washClothes: 1.8, eatHome: 1, sweepYard: 0.8 },
    day: { washClothes: 2, hangLaundry: 1.5, marketChat: 1, eatStall: 0.6, incense: 0.4 },
    dusk: { eatHome: 1.5, gossip: 1.2, watchOpera: 0.8 },
    night: NIGHT_HOME,
  },
  vendor: {
    dawn: { hawk: 1.5, eatStall: 1, sweepYard: 0.6 },
    day: { hawk: 2.5, snack: 0.5, eatStall: 0.6, gossip: 0.5 },
    dusk: { hawk: 1, eatInn: 1.2, innTea: 1, watchOpera: 0.6 },
    night: NIGHT_HOME,
  },
  cook: {
    dawn: { cook: 2, eatStall: 0.8 },
    day: { cook: 2.5, eatStall: 0.6, sitRest: 0.4 },
    dusk: { cook: 1.8, innTea: 0.8, gossip: 0.6 },
    night: NIGHT_HOME,
  },
  innkeeper: {
    dawn: { serve: 1, sweepYard: 1.2, eatHome: 1 },
    day: { serve: 2.5, eatInn: 0.8, marketChat: 0.4 },
    dusk: { serve: 2.5, innTea: 0.6 },
    night: { sleep: 2.5, innTea: 0.4 },
  },
  keeper: {
    dawn: { sweepShrine: 2, incense: 1.5 },
    day: { sweepShrine: 1.5, incense: 1, chess: 0.8, tea: 0.8, eatStall: 0.6 },
    dusk: { incense: 1.5, eatHome: 1, tea: 0.6 },
    night: NIGHT_HOME,
  },
  woodcutter: {
    dawn: { chopWood: 1.8, eatHome: 1 },
    day: { chopWood: 2.2, carryWood: 1, eatStall: 0.6, nap: 0.4 },
    dusk: { carryWood: 0.6, innTea: 1.2, eatInn: 1.2, gossip: 0.8 },
    night: NIGHT_HOME,
  },
  elder: {
    dawn: { incense: 1, pray: 0.8, tea: 1, eatHome: 1 },
    day: { chess: 2, tea: 1.2, zither: 0.8, fishRod: 0.6, storyKids: 1, nap: 0.8 },
    dusk: { storyKids: 0.8, tea: 1, innTea: 0.8, watchOpera: 1, eatHome: 1 },
    night: NIGHT_HOME,
  },
  musician: {
    dawn: { zither: 1, tea: 1, eatHome: 0.8 },
    day: { zither: 2, chess: 0.8, tea: 0.8, eatStall: 0.6 },
    dusk: { zither: 1.5, watchOpera: 1.2, innTea: 1 },
    night: { sleep: 2.5, zither: 0.4 },
  },
  child: {
    dawn: { eatHome: 1.5, helpChores: 1.2 },
    day: { chase: 1.5, hideSeek: 1.3, woodSword: 1.3, shuttle: 1.2, splash: 0.8, helpChores: 0.6, snack: 0.4 },
    dusk: { chase: 1, hideSeek: 0.8, eatHome: 1.5, watchOpera: 0.8 },
    night: { sleep: 4 },
  },
  disciple: {
    dawn: { horseStance: 1.5, drillForm: 1.5, meditateHall: 0.8, eatStall: 0.6 },
    day: { spar: 2, drillForm: 1.6, horseStance: 0.8, eatInn: 0.6, meditate: 0.5 },
    dusk: { meditate: 1.2, eatInn: 1.2, innTea: 0.6, watchOpera: 0.5 },
    night: { sleep: 3, meditate: 0.3 },
  },
  teacher: {
    dawn: { meditate: 1.5, teach: 1, drillForm: 1 },
    day: { teach: 2.5, spar: 0.8, chess: 0.5, eatInn: 0.6 },
    dusk: { meditate: 1.5, chess: 0.8, innTea: 0.8 },
    night: NIGHT_HOME,
  },
};

// ───────────────────────── people and ties ─────────────────────────

export interface LifePerson {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  /** The look (client render/looks). */
  readonly sprite: string;
  readonly female?: boolean;
  readonly child?: boolean;
  readonly job: LifeJob;
  readonly zone: ZoneId;
  /** Which home: an index into the map's homes (houses on 256 m, `SMALL_HOMES` on 72 m). */
  readonly home: number;
  /** On the 72 m map too (it holds fewer people); every person lives on the 256 m map. */
  readonly small?: boolean;
  /** The voice they speak in for greetings (data/npcActs.ts role, its talk in data/npcTalk.ts). */
  readonly role: NpcRole;
  /** Their own plan in place of the job's, act by act. */
  readonly plan?: Partial<Record<DayPart, Partial<Record<LifeActId, number>>>>;
}

const P = 'plains' as const;

export const LIFE_PEOPLE: readonly LifePerson[] = [
  // ── บ้านชาวนา (บ้านที่ 0) ──
  { id: 'dashan', name: 'ลุงต้าซาน', title: 'ชาวนา', sprite: 'farmer', job: 'farmer', zone: P, home: 0, small: true, role: 'farmer' },
  { id: 'chunhua', name: 'ป้าชุนฮวา', title: 'ชาวนา', sprite: 'farmerAunt', female: true, job: 'farmer', zone: P, home: 0, small: true, role: 'farmer' },
  { id: 'xiaozhu', name: 'เสี่ยวจู', title: 'ลูกชาวนา', sprite: 'townKid', child: true, job: 'child', zone: P, home: 0, small: true, role: 'child' },
  { id: 'xiaolan', name: 'เสี่ยวหลาน', title: 'ลูกชาวนา', sprite: 'townGirl', child: true, female: true, job: 'child', zone: P, home: 0, small: true, role: 'child' },
  // ── บ้านประมง (บ้านที่ 1) ──
  { id: 'liang', name: 'ตาเฒ่าเหลียง', title: 'ชาวประมง', sprite: 'fisherOld', job: 'fisher', zone: P, home: 1, small: true, role: 'fisher' },
  { id: 'aying', name: 'อาอิง', title: 'หญิงซักผ้า', sprite: 'innkeeper', female: true, job: 'washer', zone: P, home: 1, small: true, role: 'villager' },
  { id: 'xiaoming', name: 'เสี่ยวหมิง', title: 'หลานชาวประมง', sprite: 'fisherKid', child: true, job: 'child', zone: P, home: 1, small: true, role: 'child' },
  { id: 'achai', name: 'อาไฉ', title: 'ชาวประมงหนุ่ม', sprite: 'farmer', job: 'fisher', zone: P, home: 1, role: 'fisher' },
  // ── บ้านที่ 2: ชาวนาหนุ่มสาว ──
  { id: 'afu', name: 'อาฝู', title: 'ชาวนา', sprite: 'farmer', job: 'farmer', zone: P, home: 2, role: 'farmer' },
  { id: 'alian', name: 'อาเหลียน', title: 'ชาวนา', sprite: 'farmerAunt', female: true, job: 'farmer', zone: P, home: 2, role: 'farmer' },
  { id: 'xiaotao', name: 'เสี่ยวเถา', title: 'เด็กในเมือง', sprite: 'townKid', child: true, job: 'child', zone: P, home: 2, role: 'child' },
  // ── บ้านที่ 3: ร้านค้าและครัว ──
  { id: 'xiuhe', name: 'ป้าซิ่วเหอ', title: 'แม่ค้าผัก', sprite: 'teaSeller', female: true, job: 'vendor', zone: P, home: 3, small: true, role: 'vendor' },
  { id: 'zhangquan', name: 'ลุงจางเฉวียน', title: 'พ่อครัวแผงบะหมี่', sprite: 'merchant', job: 'cook', zone: P, home: 3, role: 'vendor' },
  { id: 'xiaoyu2', name: 'เสี่ยวอวี่', title: 'เด็กในเมือง', sprite: 'townGirl', child: true, female: true, job: 'child', zone: P, home: 3, role: 'child' },
  { id: 'aqi', name: 'อาฉี', title: 'เสี่ยวเอ้อโรงเตี๊ยม', sprite: 'townKid', job: 'innkeeper', zone: P, home: 3, role: 'innkeeper' },
  // ── บ้านที่ 4: ผู้เฒ่า คนเฝ้าศาล นักดนตรี ──
  { id: 'yang', name: 'ตาเฒ่าหยาง', title: 'ผู้เฒ่าในเมือง', sprite: 'stationMaster', job: 'elder', zone: P, home: 4, small: true, role: 'elder' },
  { id: 'song', name: 'ตาเฒ่าซ่ง', title: 'ผู้เฒ่าในเมือง', sprite: 'fisherOld', job: 'elder', zone: P, home: 4, small: true, role: 'elder' },
  { id: 'qiao', name: 'ยายเฉียว', title: 'ผู้เฒ่าในเมือง', sprite: 'farmerAunt', female: true, job: 'elder', zone: P, home: 4, role: 'elder' },
  { id: 'de', name: 'ลุงเต๋อ', title: 'คนเฝ้าศาลเจ้า', sprite: 'monk', job: 'keeper', zone: P, home: 4, small: true, role: 'monk' },
  { id: 'wenyu', name: 'แม่นางเหวินอวี้', title: 'นักดีดพิณ', sprite: 'blissLady', female: true, job: 'musician', zone: P, home: 4, role: 'performer' },
  // ── บ้านที่ 5: คนตัดไม้ หญิงซักผ้า ──
  { id: 'ahu', name: 'อาหู่', title: 'คนตัดไม้', sprite: 'farmer', job: 'woodcutter', zone: P, home: 5, role: 'villager' },
  { id: 'azhen', name: 'อาเจิน', title: 'หญิงซักผ้า', sprite: 'farmerAunt', female: true, job: 'washer', zone: P, home: 5, role: 'villager' },
  // ── โรงฝึกยุทธ์ (พักบ้านที่ 5 กับบ้านที่ 2) ──
  { id: 'qin', name: 'ครูฝึกฉิน', title: 'ครูโรงฝึกยุทธ์', sprite: 'guardOfficer', job: 'teacher', zone: P, home: 5, role: 'disciple' },
  { id: 'linfeng', name: 'หลินเฟิง', title: 'ศิษย์โรงฝึกยุทธ์', sprite: 'heroWudangPalm', job: 'disciple', zone: P, home: 2, small: true, role: 'disciple' },
  { id: 'suling', name: 'ซูหลิง', title: 'ศิษย์โรงฝึกยุทธ์', sprite: 'heroWudangFPalm', female: true, job: 'disciple', zone: P, home: 5, small: true, role: 'disciple' },
  { id: 'gaoyan', name: 'เกาเหยียน', title: 'ศิษย์โรงฝึกยุทธ์', sprite: 'heroShaolinPalm', job: 'disciple', zone: P, home: 2, role: 'disciple' },
];

export type TieKind = 'family' | 'friend' | 'rival' | 'master';

/** Who is tied to whom (both ways): family and friends seek each other out, rivals keep apart. */
export const LIFE_TIES: readonly (readonly [string, string, TieKind])[] = [
  ['dashan', 'chunhua', 'family'],
  ['dashan', 'xiaozhu', 'family'],
  ['dashan', 'xiaolan', 'family'],
  ['chunhua', 'xiaozhu', 'family'],
  ['chunhua', 'xiaolan', 'family'],
  ['xiaozhu', 'xiaolan', 'family'],
  ['liang', 'aying', 'family'],
  ['liang', 'xiaoming', 'family'],
  ['aying', 'xiaoming', 'family'],
  ['liang', 'achai', 'family'],
  ['afu', 'alian', 'family'],
  ['afu', 'xiaotao', 'family'],
  ['alian', 'xiaotao', 'family'],
  ['xiuhe', 'zhangquan', 'family'],
  ['xiuhe', 'xiaoyu2', 'family'],
  ['zhangquan', 'xiaoyu2', 'family'],
  ['yang', 'song', 'friend'],
  ['yang', 'qiao', 'friend'],
  ['song', 'liang', 'friend'],
  ['de', 'yang', 'friend'],
  ['wenyu', 'qiao', 'friend'],
  ['aying', 'azhen', 'friend'],
  ['chunhua', 'alian', 'friend'],
  ['xiuhe', 'aying', 'friend'],
  ['ahu', 'achai', 'friend'],
  ['xiaozhu', 'xiaoming', 'friend'],
  ['xiaotao', 'xiaoming', 'friend'],
  ['xiaolan', 'xiaoyu2', 'friend'],
  ['qin', 'linfeng', 'master'],
  ['qin', 'suling', 'master'],
  ['qin', 'gaoyan', 'master'],
  ['linfeng', 'suling', 'friend'],
  ['linfeng', 'gaoyan', 'rival'],
  ['song', 'zhangquan', 'rival'],
];

// ───────────────────────── lines ─────────────────────────

/** Said at work (an aside while the player is within hearing), by act; `{name}` is the player. */
export const WORK_LINES: Partial<Record<LifeActId, readonly string[]>> = {
  plough: ['ไปเถอะเจ้าทุย อีกสองแถวก็เสร็จแล้ว', 'ควายตัวนี้ดื้อแต่แรงดี', 'ดินปีนี้นุ่มดี ข้าวคงงาม'],
  plantRice: ['ปักทีละกอ ทีละกอ…', 'น้ำในนาพอดีเลย', 'ปวดหลังจริงๆ แต่ต้องให้ทันฝน'],
  harvest: ['รวงข้าวหนักดีปีนี้', 'เกี่ยวเสร็จแล้วจะได้ข้าวใหม่หุงกินกัน'],
  tillSoil: ['พรวนให้ร่วนก่อน ต้นกล้าจะได้ไม่ตาย'],
  boatNet: ['พายออกไปกลางน้ำหน่อย ปลาชุมกว่า', 'ฮึบ! แหกางสวยเลย', 'เรือโคลงหน่อยนะ อย่าตกใจ'],
  castNet: ['ฮึบ! ไปไกลๆ เลย', 'แหนี้ทอดแล้วต้องรอสักครู่', 'ปลาตัวโตติดมาแล้ว!'],
  fishRod: ['เงียบหน่อยๆ ปลาจะตกใจ', 'นั่งตกปลาแบบนี้ ใจมันสงบดี'],
  mendNet: ['ตาข่ายขาดอีกแล้ว', 'ทอทีละตา ทีละตา'],
  washClothes: ['น้ำเย็นชื่นใจดีนะ', 'คราบโคลนนี่ออกยากจริงๆ', 'ผ้าของเจ้าเด็กพวกนั้นเปื้อนทุกวัน'],
  hangLaundry: ['แดดดีอย่างนี้ เย็นนี้ก็แห้ง'],
  hawk: ['ผักสดๆ จากนาเช้านี้!', 'มาเร็ว มาเร็ว ของดีราคาถูก!', 'ซื้อสองแถมหนึ่ง วันนี้วันเดียว!'],
  cook: ['บะหมี่ร้อนๆ ได้แล้ว!', 'ไฟต้องแรง ผัดต้องเร็ว', 'ใครสั่งบะหมี่เนื้อ!'],
  serve: ['มาแล้วขอรับ ชาร้อนกับหมั่นโถว', 'โต๊ะริมหน้าต่างว่างขอรับ'],
  sweepShrine: ['กวาดลานให้สะอาด เทพเจ้าจะได้สบายใจ', 'ใบไม้ร่วงทุกวัน กวาดทุกวัน'],
  chopWood: ['ไม้ต้นนี้แข็งดี ทำคานได้', 'ฮึบ! อีกสองสามทีก็ล้ม', 'ป่าต้นน้ำเงียบผิดปกติวันนี้…'],
  chess: ['เจ้าเดินม้าตัวนั้นพลาดแล้ว', 'ฮ่าๆ รุกฆาต!', 'ช้าๆ ข้าขอคิดก่อน'],
  zither: ['เพลงนี้แต่งให้ลมใต้', 'สายพิณต้องตั้งใหม่แล้ว'],
  incense: ['ขอให้ปีนี้ฝนฟ้าตกต้องตามฤดู', 'ขอให้ลูกหลานแข็งแรง'],
  chase: ['จับได้แล้ว! ตาเจ้าเป็นคนไล่!', 'ไล่ไม่ทันหรอก!'],
  hideSeek: ['หนึ่ง สอง สาม… ซ่อนหรือยัง!', 'ชู่ว์ อย่าบอกนะว่าข้าอยู่นี่'],
  woodSword: ['ดูกระบี่!', 'ข้าคือจอมยุทธ์แห่งลานศิลา!', 'ท่านี้ข้าเห็นพี่จอมยุทธ์ใช้เมื่อวาน!'],
  shuttle: ['อย่าให้ตกนะ!', 'ข้าเตะได้สิบครั้งแล้ว!'],
  spar: ['อีกยก!', 'ระวังเท้าเจ้าด้วย', 'ฝีมือขึ้นนะเจ้า'],
  drillForm: ['ท่าที่สาม… ต้องให้ลมหายใจตามมือ', 'อีกร้อยครั้ง!'],
  horseStance: ['ขาสั่นแล้ว… อีกนิด', 'ยืนม้าให้มั่น ใจจึงมั่น'],
  teach: ['ไหล่ลง! หายใจลึกๆ', 'ช้าแต่ถูก ดีกว่าเร็วแต่ผิด', 'พรุ่งนี้ซ้อมเพิ่มอีกชั่วยาม'],
  meditate: ['…', 'ลมปราณเดินครบรอบแล้ว'],
  eatInn: ['ข้าวมันไก่ที่นี่อร่อยที่สุดในเมือง', 'กินด้วยกันสิ อาหารอร่อยขึ้นเยอะ'],
  eatStall: ['บะหมี่ลุงจางนี่แหละของดี', 'อีกชามเถอะ วันนี้เหนื่อยมาก'],
  watchOpera: ['ฉากนี้แหละที่ข้ารอ!', 'ร้องดีจริงๆ'],
  storyKids: ['เมื่อเก้าร้อยปีก่อน มีกระบี่เก้าเล่มตกลงมาจากฟ้า…', 'นั่งดีๆ แล้วตาจะเล่าต่อ'],
  nap: ['ฮ้าว… งีบสักหน่อย'],
};

/** Lines between two people at a social act (the leader says one, the other answers). */
export const CHAT_LINES: readonly (readonly [string, string])[] = [
  ['วันนี้อากาศดีนะ', 'ใช่ เหมาะจะออกไปทำนา'],
  ['ได้ยินว่าข้าวที่ตลาดขึ้นราคาอีกแล้ว', 'ปีนี้ฝนน้อย ก็อย่างนี้แหละ'],
  ['ลูกเจ้าโตเร็วจริง', 'กินเก่งยิ่งกว่าพ่อมันอีก'],
  ['เย็นนี้ไปดูงิ้วกันไหม', 'ไปสิ ข้าจะห่อขนมไปด้วย'],
  ['เมื่อคืนได้ยินเสียงหมาป่าหอน', 'ปิดประตูให้ดีล่ะ'],
  ['ศิษย์โรงฝึกยุทธ์ซ้อมกันแต่เช้าเลย', 'เสียงดังจนไก่ไม่กล้าขัน'],
];

/** Rivals passing close (one grumbles). */
export const RIVAL_LINES: readonly string[] = ['หึ… เจ้าอีกแล้ว', 'อย่ามาขวางทางข้า', 'วันหลังมาประลองกันให้รู้เรื่อง'];

/** Family or friends meeting at an act (one says). */
export const TIE_LINES: Readonly<Record<'family' | 'friend' | 'master', readonly string[]>> = {
  family: ['มากินด้วยกันสิ', 'เหนื่อยไหม นั่งพักก่อน', 'กลับบ้านพร้อมกันนะ'],
  friend: ['อ้าว มาพอดีเลย', 'นั่งด้วยกันสิสหาย', 'วันนี้ข้าเลี้ยงเอง'],
  master: ['ศิษย์คารวะอาจารย์', 'ท่าเมื่อวาน ซ้อมจนคล่องแล้วหรือยัง'],
};

// ───────────────────────── rumours ─────────────────────────

/** What people pass on about the player. */
export type RumourKind = 'saved' | 'rescue' | 'quest' | 'duelWon' | 'gift' | 'boss' | 'pk';

export interface RumourLines {
  /** Told to the player by someone who saw it. `{who}`: the person it was about (or a villager). */
  readonly seen: readonly string[];
  /** Told to the player by someone who heard it from another. */
  readonly heard: readonly string[];
  /** Said by one villager to another, of the player (`{name}`). */
  readonly gossip: readonly string[];
  /** Whether the hearer thinks better (1) or worse (−1) of the player for it. */
  readonly tone: 1 | -1;
}

export const RUMOUR_LINES: Readonly<Record<RumourKind, RumourLines>> = {
  saved: {
    seen: ['ข้าเห็นกับตา เจ้าฟันสัตว์ร้ายตัวนั้นล้มต่อหน้า{who}', 'ถ้าไม่ได้เจ้า {who}คงแย่ไปแล้ว ขอบใจนะ'],
    heard: ['ได้ยินว่าเจ้าช่วย{who}ไว้จากสัตว์ร้าย จริงหรือ', 'คนทั้งตลาดพูดกันว่าเจ้าช่วย{who}ไว้'],
    gossip: ['ได้ยินไหม {name} ช่วย{who}ไว้จากสัตว์ร้าย', 'จอมยุทธ์ {name} คนนั้นใจดีนะ ช่วย{who}ไว้'],
    tone: 1,
  },
  rescue: {
    seen: ['ข้าเห็นเจ้าเข้าไปช่วย{who}ตอนถูกรุม ใจกล้านัก', 'ถ้าไม่ได้เจ้า {who}คงไม่รอด'],
    heard: ['ได้ยินว่าเจ้าช่วย{who}ไว้ตอนถูกรุม', 'ทั้งเมืองลือกันว่าเจ้าช่วย{who}'],
    gossip: ['ได้ยินไหม {name} เข้าไปช่วย{who}ตอนถูกรุม', '{name} คนนั้นพึ่งพาได้จริงๆ ช่วย{who}ไว้'],
    tone: 1,
  },
  quest: {
    seen: ['ผู้เฒ่าชมเจ้าไม่หยุดเลย'],
    heard: ['ได้ยินว่าเจ้าช่วยงานผู้เฒ่าจนสำเร็จ เก่งจริง'],
    gossip: ['{name} ทำงานให้ผู้เฒ่าเสร็จแล้วนะ', 'ผู้เฒ่าว่า {name} พึ่งพาได้'],
    tone: 1,
  },
  duelWon: {
    seen: ['ข้าดูเจ้าประลองอยู่ ท่าสุดท้ายนั่นงามนัก'],
    heard: ['ได้ยินว่าเจ้าชนะการประลองมา', 'ลือกันทั้งเมืองว่าเจ้าประลองชนะ'],
    gossip: ['{name} ชนะการประลองอีกแล้ว', 'เห็นว่า {name} ฝีมือขึ้นเร็วมาก'],
    tone: 1,
  },
  gift: {
    seen: ['เจ้าใจดีจริงๆ ที่ให้ตำลึง{who}'],
    heard: ['ได้ยินว่าเจ้าแบ่งตำลึงให้{who} ใจกว้างนัก'],
    gossip: ['{name} ให้ตำลึง{who}ด้วยนะ', 'จอมยุทธ์ใจกว้างอย่าง {name} หายาก'],
    tone: 1,
  },
  boss: {
    seen: ['ข้าเห็นเจ้าสู้กับปีศาจตนนั้น! ยังขนลุกไม่หาย'],
    heard: ['ได้ยินว่าเจ้าปราบปีศาจที่ทุ่งลมหวนได้'],
    gossip: ['{name} ปราบปีศาจได้แล้ว!', 'ปีศาจตนนั้นล้มเพราะ {name}'],
    tone: 1,
  },
  pk: {
    seen: ['ข้าเห็นนะว่าเจ้าทำอะไรลงไป… อย่าเข้ามาใกล้'],
    heard: ['ได้ยินว่าเจ้าฆ่าคนกลางทาง… จริงหรือ', 'คนเขาลือกันว่าเจ้ามือเปื้อนเลือด'],
    gossip: ['ระวัง {name} ไว้ ได้ยินว่าฆ่าคน', '{name} คนนั้นอันตราย อย่าเข้าใกล้'],
    tone: -1,
  },
};

/** The rumour rules. */
export const RUMOUR = {
  /** Most alive at once (the oldest is forgotten first). */
  max: 16,
  /** Real seconds a rumour lives (two in-game days). */
  life: 7200,
  /** Seconds between passes for two people at a social act together. */
  contactEvery: 5,
  passChance: 0.5,
  /** Witnesses: people this close to what happened (metres). */
  witness: 13,
  /** Memory points a hearer gives for a good rumour (or takes for a bad one), once per rumour. */
  memory: 1,
} as const;

/** The people of a zone. */
export const lifePeopleOf = (zone: ZoneId, small: boolean): readonly LifePerson[] =>
  LIFE_PEOPLE.filter((p) => p.zone === zone && (!small || p.small));

/** A person's plan for a part of the day: their own over their job's. */
export function planOf(p: LifePerson, part: DayPart): Partial<Record<LifeActId, number>> {
  const own = p.plan?.[part];
  return own ? { ...JOB_PLANS[p.job][part], ...own } : JOB_PLANS[p.job][part];
}

/** Whether a person may do an act at all (by job, age and the martial needs). */
export function canDo(p: LifePerson, id: LifeActId): boolean {
  const a: LifeActDef = LIFE_ACTS[id];
  if (a.jobs) return a.jobs.includes(p.job);
  return !p.child;
}
